// What did they say? A transcript from Palefò's ear → { amount, currency }.
//
// Open transcription of spoken Kreyòl does not come back tidy. Measured against
// the live endpoint, the same kind of sentence returns as Kreyòl ("De mil
// senksan goud."), as French ("Cinq cents dollars américains.") or as English
// ("Ninety-five dollars."), with words glued together ("twason") and the odd
// consonant swapped ("anboud" for "an goud"). So Kreyòl, French and English are
// read together, glued words are pulled apart, and currency words match loosely.
//
// The other voice languages each read their own number words, taken from the
// composer (src/shared/speech.mjs): whatever Kòb pa m can say, it can hear.

import { kreyolUnder100, numberWords } from '../shared/speech.mjs';

export const MAX_AMOUNT = 999999999;

// Lowercase, without accents: "Dólares" → "dolares", "dreißig" → "dreissig", "гаитянский" → "гаитянскии".
const strip = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').normalize('NFC').replace(/ß/g, 'ss');
const WORD = /^[a-zа-я]+$/;

// ── number words ──
//
// A vocabulary is { small: word → 0..999, scale: word → 100 | 1e3 | 1e6 | 1e9,
// loose, fillers, ambiguous }. Rows are [value, ...words].

function vocabulary({ small = [], scale = [], loose = [], fillers = [], ambiguous = [] }) {
  const map = (rows) => new Map(rows.flatMap(([value, ...words]) => words.map((w) => [strip(w), value])));
  return { small: map(small), scale: map(scale), loose: new Set(loose), fillers: new Set(fillers), ambiguous: new Set(ambiguous.map(strip)) };
}

// Kreyòl, French and English, read as one: this is what a Kreyòl speaker's words come back as.
const FAMILY = vocabulary({
  small: [
    ...Array.from({ length: 100 }, (_, n) => [n, kreyolUnder100(n), kreyolUnder100(n).replace(/nn/g, 'n')]),   // "vende" for "vennde"
    // Kreyòl as it gets spelled back, including the stems of split tens ("swasann dis").
    [1, 'yon', 'youn', 'yonn'], [5, 'sek', 'senq'], [8, 'wit'], [18, 'dizwit'],
    [20, 'vin', 'vent', 'venn'], [30, 'trann', 'tran'], [40, 'karann', 'karan'],
    [50, 'senkann', 'senkan'], [60, 'swasann', 'swasan'], [80, 'katrevin'],
    ...['zero', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze',
      'treize', 'quatorze', 'quinze', 'seize'].map((w, n) => [n, w]),
    [1, 'une'], [20, 'vingt', 'vingts'], [30, 'trente'], [40, 'quarante'], [50, 'cinquante'], [60, 'soixante'],
    ...['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen',
      'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'].map((w, i) => [i + 1, w]),
    ...['twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'].map((w, i) => [(i + 2) * 10, w]),
  ],
  scale: [
    [100, 'san', 'cent', 'cents', 'hundred'], [1e3, 'mil', 'mille', 'milles', 'thousand'],
    [1e6, 'milyon', 'million', 'millions'], [1e9, 'milya', 'milliard', 'milliards', 'billion'],
  ],
  // "twason", "senksant": a hundred only as the tail of a glued word, never alone.
  loose: ['son', 'sant'],
  fillers: ['e', 'et', 'and', 'ak'],
  // Number words that are ordinary words too ("combien de…", "m vin chanje…",
  // "san" = without). Alone and away from a currency, they only count when
  // nothing better does.
  ambiguous: ['en', 'un', 'une', 'yon', 'youn', 'de', 'one', 'vin', 'san'],
});

// A language's own number words: every number under a thousand that it writes
// as one word, straight from the composer, plus the forms the composer never
// produces on their own. `respell` adds a second spelling of each.
const under1000 = (lang, respell = (w) => w) => Array.from({ length: 1000 }, (_, n) => [n, respell(strip(numberWords(n, lang)))])
  .filter(([, w]) => WORD.test(w));
const derived = (lang, extra) => vocabulary({ ...extra, small: [...under1000(lang), ...extra.small] });
// German and Italian write a number as a single word; the composer spaces it for the voice. Read both.
const joined = (w) => w.replace(/ /g, '');
// doscientas, duzentas: the hundreds in front of a feminine noun.
const feminine = (lang) => [2, 3, 4, 5, 6, 7, 8, 9].map((h) => [h * 100, numberWords(h * 100, lang).replace(/os$/, 'as')]);

const VOCABULARY = {
  ht: FAMILY, en: FAMILY, fr: FAMILY,
  zh: FAMILY, ja: FAMILY, ko: FAMILY,               // their numerals are read by the tokenizer; this is for "500 dollars"
  es: derived('es', {
    small: [[1, 'un', 'una'], [21, 'veintiún', 'veintiuna'], [100, 'ciento'], ...feminine('es')],
    scale: [[1e3, 'mil'], [1e6, 'millón', 'millones']],
    fillers: ['y'], ambiguous: ['un', 'una', 'uno'],
  }),
  pt: derived('pt', {
    small: [[1, 'uma'], [2, 'duas'], [100, 'cento'], [14, 'quatorze'], [16, 'dezasseis'], [17, 'dezassete'],
      [19, 'dezanove'], ...feminine('pt')],
    scale: [[1e3, 'mil'], [1e6, 'milhão', 'milhões'], [1e9, 'bilhão', 'bilhões']],
    fillers: ['e'], ambiguous: ['um', 'uma'],
  }),
  it: derived('it', {
    // "centottanta" beside "centoottanta": both spellings are in use.
    small: [[1, 'un', 'una'], ...under1000('it', joined), ...under1000('it', (w) => joined(w).replace('centoo', 'cento'))],
    scale: [[1e3, 'mille', 'mila'], [1e6, 'milione', 'milioni'], [1e9, 'miliardo', 'miliardi']],
    fillers: ['e'], ambiguous: ['un', 'una', 'uno'],
  }),
  de: derived('de', {
    small: [[1, 'ein', 'eine', 'einen', 'einem', 'einer'], ...under1000('de', joined)],
    scale: [[100, 'hundert'], [1e3, 'tausend'], [1e6, 'million', 'millionen'], [1e9, 'milliarde', 'milliarden']],
    fillers: ['und'], ambiguous: ['ein', 'eine', 'einen', 'einem', 'einer', 'eins'],
  }),
  ru: derived('ru', {
    small: [[0, 'нуль'], [1, 'одна', 'одно'], [2, 'две']],
    scale: [[1e3, 'тысяча', 'тысячи', 'тысяч', 'тысячу'], [1e6, 'миллион', 'миллиона', 'миллионов'],
      [1e9, 'миллиард', 'миллиарда', 'миллиардов']],
    ambiguous: ['один', 'одна', 'одно'],
  }),
};

const HALVES = new Set(['edmi', 'demi', 'half']);

// ── currency words, in every language at once ──

const DOLA = /^(dol+(a|ar|ars|as|ares|aro|ari)|доллар(а|ов|ы)?|ドル|달러|달라|元)$/;
const GOUD = /^((an)?[gbp]ou(d|de|des|ds|rd|rde|rdes)|гурд(а|ов|ы)?)$/;
const GOUD_WORDS = new Set(['pyas', 'pias', 'piastre', 'piastres', 'htg', '古德', '古尔德', '海地古德', 'グールド', 'グルド',
  '구르드', '구루드', '고르드', '굴드']);
const USD_WORDS = new Set(['ameriken', 'meriken', 'amerikin', 'americain', 'americains', 'americaine', 'americaines',
  'american', 'us', 'usd', 'usa', 'vet', 'etazini', 'estadounidense', 'estadounidenses', 'americano', 'americanos',
  'americana', 'americanas', 'norteamericano', 'norteamericanos', 'statunitense', 'statunitensi', 'americani',
  'amerikanische', 'amerikanischer', 'amerikanischen', 'сша', '미국']);
const HTD_WORDS = new Set(['ayisyen', 'aysyen', 'ayisien', 'haitien', 'haitiens', 'haitienne', 'haitian', 'ayiti',
  'haiti', 'ht', 'htd', 'haitiano', 'haitianos', 'haitiana', 'haitianas', 'haitiani', 'haitianische',
  'haitianischer', 'haitianischen', '아이티']);
const CAD_WORDS = new Set(['kanadyen', 'kanadyin', 'canadien', 'canadiens', 'canadienne', 'canadiennes', 'canadian',
  'kanada', 'canada', 'canadiense', 'canadienses', 'canadense', 'canadenses', 'kanadische', 'kanadischer',
  'kanadischen', '加拿大', 'カナダ', '캐나다']);
// The euro and the peso are a currency in one word; the only peso here is the Dominican one.
const EURO = /^(ewo|euro|euros|euri|evro|евро|ユーロ|유로|欧元)$/;
const PESO = /^(peso|pesos|песо|ペソ|페소|比索)$/;
// A currency complete in one word: 美元 is the US dollar, ハイチドル the Haitian one.
const WHOLE = new Map([['usd', 'usd'], ['htd', 'htd'], ['美元', 'usd'], ['美金', 'usd'], ['米ドル', 'usd'],
  ['アメリカドル', 'usd'], ['海地元', 'htd'], ['ハイチドル', 'htd'], ['eur', 'eur'], ['cad', 'cad'], ['dop', 'dop'],
  ['加元', 'cad'], ['加拿大元', 'cad'], ['カナダドル', 'cad'], ['多米尼加比索', 'dop'], ['ドミニカペソ', 'dop']]);
const DETERMINERS = new Set(['a', 'la', 'an', 'lan', 'yo']);

const isGoud = (w) => GOUD.test(w) || GOUD_WORDS.has(w);
const isCurrency = (w) => !!w && (DOLA.test(w) || isGoud(w) || WHOLE.has(w) || EURO.test(w) || PESO.test(w));
const qualifies = (w) => (!w ? null
  : USD_WORDS.has(w) || w.startsWith('американск') ? 'usd'
  : HTD_WORDS.has(w) || w.startsWith('гаитянск') ? 'htd'
  : CAD_WORDS.has(w) || w.startsWith('канадск') ? 'cad' : null);

// ── tokens ──

// Chinese, Japanese and Korean numerals are read here, a whole run at a time:
// 六万五千四百三十五, 육만 오천사백삼십오, and "5万" with its digits.
const HAN = '零〇一二两兩三四五六七八九十百千万萬亿億';
const HANGUL = '영공일이삼사오육칠팔구십백천만억';
const DIGIT = Object.fromEntries([
  ...[...'零一二三四五六七八九'].map((c, i) => [c, i]), ['〇', 0], ['两', 2], ['兩', 2],
  ...[...'영일이삼사오육칠팔구'].map((c, i) => [c, i]), ['공', 0],
]);
const PLACE = { 十: 10, 百: 100, 千: 1e3, 십: 10, 백: 100, 천: 1e3 };
const MYRIAD = { 万: 1e4, 萬: 1e4, 亿: 1e8, 億: 1e8, 만: 1e4, 억: 1e8 };

function eastValue(run) {
  let total = 0, section = 0, pending = 0;
  for (const [, digits, c] of run.matchAll(/(\d+(?:\.\d+)?)|(\S)/g)) {
    if (digits) pending = parseFloat(digits);
    else if (c in DIGIT) pending = pending * 10 + DIGIT[c];
    else if (c in PLACE) { section += (pending || 1) * PLACE[c]; pending = 0; }
    else { total += (section + pending || 1) * MYRIAD[c]; section = 0; pending = 0; }
  }
  return total + section + pending;
}

// Words these languages write with no space around them, so they are found by name. Longest first.
const EAST_WORDS = ['海地古德', '海地元', '美元', '美金', '古尔德', '古德', 'アメリカドル', 'ハイチドル', '米ドル', 'グールド', 'グルド',
  '多米尼加比索', '加拿大元', '加拿大', '加元', '欧元', '比索', 'ドミニカペソ', 'カナダドル', 'カナダ', 'ユーロ', 'ペソ',
  'ドル', '아이티', '미국', '캐나다', '구르드', '구루드', '고르드', '굴드', '달러', '달라', '유로', '페소', '元'];
const NUM = '\\d+(?:\\.\\d+)?';
const TOKEN = new RegExp([
  `(${EAST_WORDS.join('|')})`,
  `((?:(?:${NUM})?[${HAN}]+)+)`,
  // A Korean numeral run ends the word it is in, or runs into a currency: 오백달러, but not the 오 of 오늘.
  `((?:${NUM})?[${HANGUL}]+(?:\\s?(?:${NUM})?[${HANGUL}]+)*)(?=$|[^가-힣]|달러|달라|구르드|구루드|고르드|굴드|미국|아이티|캐나다|유로|페소)`,
  // "1,500.50", "1 500", "1.500,50" (grouped thousands), or plain "500" / "12.5".
  '(\\d{1,3}(?:[ ,.]\\d{3})+(?!\\d)(?:[.,]\\d{1,2}(?!\\d))?)',
  '(\\d+(?:[.,]\\d{1,2}(?!\\d))?)',
  '([a-zа-я]+)',
].join('|'), 'g');

function tokenize(text) {
  const s = strip(String(text || '')).replace(/\$/g, ' dola ').replace(/€/g, ' euro ');
  const tokens = [];
  for (const match of s.matchAll(TOKEN)) {
    const [, east, han, hangul, grouped, plain, word] = match;
    if (east || word) tokens.push({ w: east || word });
    else if (han) tokens.push({ num: eastValue(han), weak: han.length === 1 });   // a lone 一 is often not a number
    else if (hangul) {
      // …and starts the word it is in: the 일 of 마일 is not a one.
      if (!/[가-힣]/.test(s[match.index - 1] || '')) tokens.push({ num: eastValue(hangul), weak: hangul.length === 1 });
    } else if (plain) tokens.push({ num: parseFloat(plain.replace(',', '.')) });
    else {
      const [, whole, cents] = grouped.match(/^(.*?)(?:[.,](\d{1,2}))?$/);
      tokens.push({ num: parseFloat(`${whole.replace(/[ ,.]/g, '')}.${cents || 0}`) });
    }
  }
  return tokens;
}

/**
 * "senksan" → senk + san, "duemilacinquecento" → due + mila + cinquecento.
 * The longest number word first; null unless a scale word is in there.
 */
function unglue(w, V, depth = 0) {
  for (let i = w.length - 2; i >= 2; i--) {
    const a = w.slice(0, i), b = w.slice(i);
    if (!V.small.has(a) && !V.scale.has(a)) continue;
    if (V.scale.has(b) || V.loose.has(b) || (V.small.has(b) && V.scale.has(a))) return [a, b];
    const rest = depth < 3 ? unglue(b, V, depth + 1) : null;
    if (rest) return [a, ...rest];
  }
  return null;
}

function expand(tokens, V) {
  const known = (w) => V.small.has(w) || V.scale.has(w) || isCurrency(w);
  const out = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i], next = tokens[i + 1]?.w || '';
    if (t.w === 'u' && next === 's') { out.push({ w: 'us' }); i++; continue; }
    if ((t.w === 'quatre' || t.w === 'katre') && /^(vingts?|ven|vin)$/.test(next)) { out.push({ w: 'katreven' }); i++; continue; }
    const parts = t.w && !known(t.w) ? unglue(t.w, V) : null;
    if (parts) parts.forEach((w) => out.push({ w, glued: true }));
    else out.push(t);
  }
  return out;
}

// ── numbers ──

// May `v` follow a number word worth `prev` inside one number?
const chains = (prev, v) =>
  (prev >= 100 && v < 100) ||                                 // quinientos veinte, пятьсот двадцать
  (prev >= 20 && prev % 10 === 0 && v >= 1 && v < 10) ||      // ven twa, twenty five
  ((prev === 60 || prev === 80) && v >= 10 && v < 20) ||      // swasant onz, katreven kenz
  (prev === 10 && v >= 7 && v <= 9);                          // dix-sept

// French words that are Kreyòl numbers, in front of a currency. On its own,
// "en gourdes" is never "one gourde" (that is "yon goud" — though "san en goud"
// is a hundred and one), and "de gourdes" is "of gourdes" where the Kreyòl "de
// goud" is two: "combien de gourdes", "un million de gourdes".
const falseFriend = (w, next, run) => isCurrency(next) && (
  (w === 'en' && !run)
  || (w === 'de' && (run ? run.after === 'scale' && run.top >= 1e6 && !run.cur : !/^(goud|dola)$/.test(next)))
);

/** Every number in the phrase, in order: [{ value, start, end, ambiguous }] over token positions. */
function numberRuns(tokens, V) {
  const isNumeric = (t) => !!t && (t.num != null || V.small.has(t.w) || V.scale.has(t.w));
  const runs = [];
  let run = null;
  const close = () => {
    if (!run) return;
    runs.push({ value: run.total + run.cur, start: run.start, end: run.end, ambiguous: run.n === 1 && run.weak });
    run = null;
  };
  const open = (i) => { run = { total: 0, cur: 0, after: null, prev: 0, top: Infinity, start: i, end: i, n: 0, weak: false }; };

  tokens.forEach((t, i) => {
    const scale = t.w && (V.scale.get(t.w) || (t.glued && V.loose.has(t.w) ? 100 : 0));
    if (t.num != null) {
      // Digits join a run only straight after a scale word: "2 mil 500".
      const joins = run && run.after === 'scale' && Number.isInteger(t.num) && t.num < (run.cur ? 100 : 1000);
      if (!joins) { close(); open(i); }
      run.cur += t.num; run.after = 'digits';
    } else if (V === FAMILY && falseFriend(t.w, tokens[i + 1]?.w, run)) {
      close(); return;
    } else if (V.small.has(t.w)) {
      const v = V.small.get(t.w);
      if (!run || !(run.after === 'scale' || (run.after === 'small' && chains(run.prev, v)))) { close(); open(i); }
      run.cur += v; run.prev = v; run.after = 'small';
    } else if (scale === 100) {
      if (run && run.cur >= 100) close();
      if (!run) open(i);
      run.cur = (run.cur || 1) * 100; run.after = 'scale';
    } else if (scale) {
      if (run && scale === run.top) close();
      if (!run) open(i);
      // Scales descend (milyon … mil) — or a larger one multiplies what came before: dos mil millones.
      if (scale > run.top) run.total = (run.total + run.cur) * scale;
      else run.total += (run.cur || 1) * scale;
      run.cur = 0; run.top = scale; run.after = 'scale';
    } else if (run && V.fillers.has(t.w) && isNumeric(tokens[i + 1])) {
      return;                                   // "vingt et un", "one hundred and five", "trinta e cinco"
    } else { close(); return; }
    run.end = i + 1; run.n++;
    if (run.n === 1) run.weak = !!t.weak || V.ambiguous.has(t.w);
  });
  close();
  return runs;
}

// ── currencies ──

/** Every currency named in the phrase, in order: [{ at, currency }], currency null for a bare "dola". */
function currencyMentions(tokens) {
  const mentions = [], used = new Set();
  const qualifier = (i) => (used.has(i) ? null : qualifies(tokens[i]?.w));
  tokens.forEach((t, i) => {
    if (!t.w || !DOLA.test(t.w)) return;
    // "dola ameriken", "dola a ayisyen", "dola edmi ameriken", "US dollars", "미국 달러"
    const between = tokens[i + 1]?.w;
    const q = [i + 1, DETERMINERS.has(between) || HALVES.has(between) ? i + 2 : -1, i - 1].find((j) => qualifier(j));
    mentions.push({ at: i, currency: q == null ? null : qualifier(q) });
    used.add(i); if (q != null) used.add(q);
  });
  tokens.forEach((t, i) => {
    if (!t.w || used.has(i)) return;
    if (isGoud(t.w)) mentions.push({ at: i, currency: 'htg' });
    else if (EURO.test(t.w)) mentions.push({ at: i, currency: 'eur' });
    else if (PESO.test(t.w)) mentions.push({ at: i, currency: 'dop' });
    else if (WHOLE.has(t.w)) mentions.push({ at: i, currency: WHOLE.get(t.w) });
    else if (t.w === 'us') mentions.push({ at: i, currency: 'usd' });
  });
  return mentions.sort((a, b) => a.at - b.at);
}

/**
 * "Dola" with nothing after it — American, or Haitian?
 *
 * In Haiti a price in "dola" is in Haitian dollars (5 goud each) and the
 * American kind is named: "dola ameriken", "dola vèt", "US". Abroad the habit
 * runs the other way. Nothing in a transcript settles it, so this follows the
 * setting ("Dola" poukont li, in Reglaj) and the screen offers the other
 * reading in one tap. `amount` and `words` are here for a smarter rule.
 */
// eslint-disable-next-line no-unused-vars
export function resolveBareDola({ preference, amount, words }) {
  return preference === 'usd' ? 'usd' : 'htd';
}

// A question is for the Assistant, not a change of currency: "ki to ewo a?", "how much is a euro?".
const QUESTION_WORDS = new Set(['ki', 'kisa', 'kijan', 'kilè', 'konbe', 'konben', 'poukisa', 'eske', 'what', 'how', 'much',
  'many', 'why', 'when', 'which', 'is', 'quel', 'quelle', 'combien', 'comment', 'pourquoi', 'que', 'cuanto', 'cuantos',
  'cuanta', 'que', 'como', 'por', 'quanto', 'quantos', 'quanta', 'wie', 'was', 'warum', 'сколько', 'что', 'как', 'почему',
  '多少', '什么', '为什么', 'いくら', '何', 'なぜ', '얼마', '무엇', '왜']);
const FOREIGN = new Set(['usd', 'eur', 'cad', 'dop']);

/**
 * An utterance that asks for another foreign currency and nothing else —
 * "ewo", "mete dola kanadyen", "chanje an peso", "euros, please", "en euros",
 * "切换到欧元" — read as that currency's code. Null when it is not one: an
 * amount in it, a question word, gourdes or Haitian dollars, or two foreign
 * currencies. A bare "dola" counts only where it would be read as the US one.
 */
export function switchRequest(text, { bareDola = 'htd', lang = 'ht' } = {}) {
  const V = VOCABULARY[lang] || FAMILY;
  const tokens = expand(tokenize(text), V);
  if (!tokens.length || numberRuns(tokens, V).some((r) => !r.ambiguous)) return null;
  if (tokens.some((t) => t.w && QUESTION_WORDS.has(t.w))) return null;
  const mentions = currencyMentions(tokens);
  if (!mentions.length) return null;
  const words = tokens.map((t) => t.w || String(t.num));
  const named = [...new Set(mentions.map((m) => m.currency || resolveBareDola({ preference: bareDola, words })))];
  return named.length === 1 && FOREIGN.has(named[0]) ? named[0] : null;
}

/** The currencies a phrase names, amount or not — "kisa dola ayisyen an ye?" names 'htd'. A bare "dola" is left out. */
export function namedCurrencies(text, { lang = 'ht' } = {}) {
  const mentions = currencyMentions(expand(tokenize(text), VOCABULARY[lang] || FAMILY));
  return [...new Set(mentions.map((m) => m.currency).filter(Boolean))];
}

/**
 * Read an utterance spoken in the voice language `lang`. Returns
 *   { ok: true, amount, currency, target, bareDola }
 * where `currency` is 'usd' | 'htg' | 'htd' | 'eur' | 'cad' | 'dop', or null when none was named;
 * `target` is a second currency they asked for ("… ye an goud"), or null;
 * `bareDola` says the currency was an unqualified "dola", resolved by setting.
 * Or { ok: false, reason: 'no_amount' | 'too_large' }.
 */
export function parseMoney(text, { bareDola = 'htd', lang = 'ht' } = {}) {
  const V = VOCABULARY[lang] || FAMILY;
  const tokens = expand(tokenize(text), V);
  const runs = numberRuns(tokens, V);
  if (!runs.length) return { ok: false, reason: 'no_amount' };

  const mentions = currencyMentions(tokens);
  // The amount is the first number with a currency beside it: straight after it,
  // one word on ("twa san bèl dola"), or just before ("$500"). Failing that, the
  // first number that is not an ordinary word in disguise.
  const named = (r) => mentions.some((m) => m.at === r.end || m.at === r.start - 1
    || (m.at === r.end + 1 && !runs.some((other) => other.start === r.end)));
  const run = runs.find(named) || runs.find((r) => !r.ambiguous) || runs[0];

  let amount = run.value;
  if (tokens.slice(run.end).some((t) => HALVES.has(t.w))) amount += 0.5;
  amount = Math.round(amount * 100) / 100;
  if (amount > MAX_AMOUNT) return { ok: false, reason: 'too_large' };

  const source = mentions.find((m) => m.at === run.end || m.at === run.end + 1)
    || mentions.find((m) => m.at === run.start - 1)
    || mentions.find((m) => m.at >= run.end)
    || mentions[0] || null;
  const words = tokens.map((t) => t.w || String(t.num));
  const resolve = (m) => m.currency || resolveBareDola({ preference: bareDola, amount, words });
  const currency = source ? resolve(source) : null;
  const other = mentions.find((m) => m !== source && resolve(m) !== currency);

  return {
    ok: true,
    amount,
    currency,
    target: other ? resolve(other) : null,
    bareDola: !!source && !source.currency,
  };
}
